import React, { useState } from 'react';
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, CssBaseline, Box, Toolbar, Container, Paper, 
    useMediaQuery, Link, IconButton, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams } from "react-router-dom";
import { 
    Email as EmailIcon,
    ContentCopy as ContentCopyIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

import * as CONSTANTS from "../constants"

const ContactUs = () => {

    document.title = `${CONSTANTS.TITLE} - Contact Us`;

    let { lang } = useParams();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const email = "wotageizone@gmail.com";

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <CssBaseline />
            <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
            <Box sx={{ display: 'flex' }}>
                <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                <Box component="main" sx={{ flexGrow: 1 }}>
                    <Toolbar />
                    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
                        {/* Hero Section */}
                        <Box
                            sx={{
                                position: 'relative',
                                minHeight: isMobile ? '200px' : '280px',
                                width: "100%",
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 3,
                                overflow: 'hidden',
                                mb: 5,
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Container maxWidth="md">
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography
                                        variant={isMobile ? "h4" : "h2"}
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 2,
                                            color: 'text.primary',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {CONSTANTS.CONTACT_US.TITLE[lang]}
                                    </Typography>
                                    <Typography
                                        variant={isMobile ? "body1" : "h6"}
                                        sx={{ 
                                            color: 'text.secondary',
                                            maxWidth: '600px',
                                            mx: 'auto',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {CONSTANTS.CONTACT_US.SUBTITLE[lang]}
                                    </Typography>
                                </Box>
                            </Container>
                        </Box>

                        {/* Email Contact Card */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
                            <Paper 
                                elevation={3}
                                sx={{ 
                                    p: isMobile ? 3 : 5, 
                                    textAlign: 'center',
                                    maxWidth: '600px',
                                    width: '100%',
                                    borderRadius: 3,
                                    background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                                    border: `1px solid ${theme.palette.divider}`,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                    }
                                }}
                            >
                                <Box 
                                    sx={{ 
                                        width: 80, 
                                        height: 80, 
                                        borderRadius: '50%', 
                                        bgcolor: `${theme.palette.primary.main}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3
                                    }}
                                >
                                    <EmailIcon 
                                        sx={{ 
                                            fontSize: 40, 
                                            color: 'primary.main'
                                        }} 
                                    />
                                </Box>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 600,
                                        mb: 1,
                                        color: 'text.primary'
                                    }}
                                >
                                    {CONSTANTS.CONTACT_US.EMAIL_TITLE[lang]}
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    color="text.secondary"
                                    sx={{ mb: 3 }}
                                >
                                    {CONSTANTS.CONTACT_US.EMAIL_DESCRIPTION[lang]}
                                </Typography>
                                
                                {/* Email with Copy Button */}
                                <Box 
                                    sx={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        p: 2,
                                        bgcolor: `${theme.palette.primary.main}08`,
                                        borderRadius: 2,
                                        border: `1px dashed ${theme.palette.primary.main}40`,
                                    }}
                                >
                                    <Link 
                                        href={`mailto:${email}`}
                                        underline="hover"
                                        sx={{ 
                                            fontSize: isMobile ? '1.1rem' : '1.4rem',
                                            fontWeight: 500,
                                            color: 'primary.main',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {email}
                                    </Link>
                                    <Tooltip title={copied ? "Copied!" : "Copy email"}>
                                        <IconButton 
                                            onClick={handleCopyEmail}
                                            size="small"
                                            sx={{ 
                                                color: copied ? 'success.main' : 'primary.main',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Footer Text */}
                        {/* <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    maxWidth: '500px',
                                    mx: 'auto',
                                    lineHeight: 1.8
                                }}
                            >
                                {CONSTANTS.CONTACT_US.FOLLOW_DESCRIPTION[lang]}
                            </Typography>
                        </Box> */}
                    </Container>
                </Box>
            </Box>
        </Box>
    );
};
 
export default ContactUs;

